import React, { useEffect, useState } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import cardTexture from '../assets/card_texture.jpg';

// Audio helpers (placeholders)
const playSwipeSound = () => {
    console.log("🔊 Sound: shhh-ca-LUNK");
};

const playSuccessChime = () => {
    console.log("🔊 Sound: Retro Chime ding!");
};

const UnlockSequence = ({ onUnlockComplete, mode = 'unlock', tier = 'gold' }) => {
    const [ledState, setLedState] = useState('off'); // off, red, green

    // Animation controls
    const readerControls = useAnimation();
    const cardControls = useAnimation();
    const modalControls = useAnimation();

    const isExecutive = tier === 'executive';

    // Card Styles
    const cardBackground = isExecutive
        ? 'linear-gradient(135deg, #1a1a1a, #000000)'
        : 'linear-gradient(135deg, #bf953f, #fcf6ba, #b38728, #fbf5b7, #aa771c)';

    const cardBorder = isExecutive ? '#333' : '#b38728';
    const textColor = isExecutive ? '#d4af37' : '#5c4010';
    const labelText = isExecutive ? 'EXECUTIVE // LEVEL 9' : 'GOLD MEMBER // LEVEL 5';

    useEffect(() => {
        if (mode === 'eject') {
            runEjectSequence();
        } else {
            runUnlockSequence();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);


    const runEjectSequence = async () => {
        // --- Setup Initial State for Eject ---
        // Reader is visible, Card is inserted
        setLedState('green');
        readerControls.set({ opacity: 1, scale: 1, rotateX: 0 });
        cardControls.set({ y: -180, scale: 1, opacity: 1 });
        modalControls.set({ opacity: 0 });

        // Wait a beat
        await new Promise(resolve => setTimeout(resolve, 500));

        // --- Step 1: Eject Action ---
        playSwipeSound(); // or a reverse sound

        // Push card out (Down)
        await cardControls.start({
            y: 200,
            transition: {
                duration: 0.8,
                ease: [0.42, 0, 1, 1]
            }
        });

        // --- Step 2: Fade Out Reader ---
        await readerControls.start({
            opacity: 0,
            scale: 0.9,
            transition: { duration: 0.5 }
        });

        if (onUnlockComplete) onUnlockComplete();
    };

    const runUnlockSequence = async () => {
        // --- Step 1: The Transformation ---
        modalControls.start({ opacity: 0, scale: 0.95, transition: { duration: 0.3 } });

        // Bring in the Reader overlay
        setLedState('red');
        await readerControls.start({
            opacity: 1,
            scale: 1,
            rotateX: 0,
            transition: { type: "spring", damping: 12, stiffness: 100, delay: 0.2 }
        });

        // --- Step 2: Keycard Appearance ---
        // Slide card up from bottom
        await cardControls.start({
            y: 0,
            opacity: 1,
            transition: { type: "spring", damping: 15, stiffness: 80 }
        });

        // Short pause for anticipation
        await new Promise(resolve => setTimeout(resolve, 300));

        // --- Step 3: The Swipe Action (Climax) ---
        playSwipeSound();

        // The key here for "insertion" is that the card is z-index 10, Reader Base is 0, Reader Lip is 20.
        // Moving Y up moves it visually "into" the slot.
        await cardControls.start({
            y: [0, 10, -180], // Slight pullback (down) then hard push (up) into slot
            scale: [1, 1.02, 0.98],
            transition: {
                y: {
                    times: [0, 0.2, 1], // Slow start, fast finish insertion
                    duration: 0.6,
                    ease: [0.42, 0, 1, 1]
                },
                scale: { duration: 0.6 }
            }
        });

        // --- Step 4: Validation ---
        await new Promise(resolve => setTimeout(resolve, 100));
        setLedState('green');
        playSuccessChime();

        // --- Step 5: The Reveal ---
        await new Promise(resolve => setTimeout(resolve, 800));

        // Dissolve reader and card
        cardControls.start({ opacity: 0, transition: { duration: 0.3 } });
        await readerControls.start({
            opacity: 0,
            scale: 1.05,
            filter: "blur(10px)",
            transition: { duration: 0.6, ease: "easeOut" }
        });

        if (onUnlockComplete) onUnlockComplete();
    };

    const ledVariants = {
        off: { backgroundColor: '#300', boxShadow: 'none' },
        red: {
            backgroundColor: '#ef4444',
            boxShadow: '0 0 15px rgba(239, 68, 68, 0.8), inset 0 0 5px rgba(254, 202, 202, 0.5)',
        },
        green: {
            backgroundColor: '#22c55e',
            boxShadow: '0 0 20px rgba(34, 197, 94, 1), inset 0 0 5px rgba(187, 247, 208, 0.8)',
            transition: { duration: 0.2 }
        }
    };

    return (
        <div className="relative w-full h-[400px] flex justify-center items-center overflow-visible z-50 pointer-events-none perspective-[1000px]">

            {/* Scaling Container: Ensures the fixed-pixel art scales down on mobile */}
            <div className="relative w-[340px] h-[400px] flex justify-center items-center scale-[0.8] sm:scale-100 transition-transform origin-center">

                <AnimatePresence>
                    {/* --- Restricted Modal (Initial State) --- */}
                    <motion.div
                        className="absolute top-[30%] w-[90%] h-48 bg-[#e8e0d0] border-2 border-[#d0c8b8] rounded-lg shadow-sm flex flex-col justify-center items-center text-[#8a8070] z-50"
                        initial={{ opacity: 1, scale: 1 }}
                        animate={modalControls}
                    >
                        <div className="text-4xl mb-4 opacity-70">🔒</div>
                        <div className="font-mono tracking-widest text-xs font-bold">RESTRICTED ACCESS</div>
                    </motion.div>

                    {/* --- Card Reader Assembly --- */}
                    <motion.div
                        className="absolute top-[20%] w-[320px] h-auto flex flex-col items-center z-40"
                        initial={{ opacity: 0, scale: 0.8, rotateX: 20 }}
                        animate={readerControls}
                        style={{ transformStyle: 'preserve-3d' }}
                    >

                        {/* LAYER 1: Reader Base (The Back) */}
                        <div
                            className="absolute w-full h-[180px] rounded border border-gray-600 z-0"
                            style={{
                                background: `linear-gradient(to bottom, #dcdcdc, #aaaaaa 40%, #777777 100%)`,
                                boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                            }}
                        ></div>

                        {/* LED on Base */}
                        <motion.div
                            className="absolute top-4 right-5 w-3 h-3 rounded-full border border-gray-600 z-30"
                            variants={ledVariants}
                            initial="off"
                            animate={ledState}
                        />

                        <div className="absolute top-5 w-full text-center font-bold text-[#444] text-sm tracking-widest shadow-white drop-shadow-[0_1px_0_rgba(255,255,255,0.5)] z-0">
                            PARADISE TERMINAL
                        </div>


                        {/* LAYER 2: The Reader Lip Base (The Bottom Shelf) */}
                        <div className="absolute top-[80px] w-full h-[60px] z-10 pointer-events-none">
                            <div
                                className="w-[90%] mx-auto h-12 bg-[#333] rounded-b-xl rounded-t-md shadow-[0_5px_10px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.2)] relative border-t border-gray-500"
                                style={{
                                    background: 'linear-gradient(to bottom, #444, #222)'
                                }}
                            >
                                {/* The Slit Background (Black Void) */}
                                <div className="absolute bottom-2 left-[10%] w-[80%] h-1.5 bg-black rounded-full shadow-[inset_0_1px_3px_rgba(0,0,0,1)]"></div>
                            </div>
                        </div>

                        {/* LAYER 3: The Card (Sandwiched) */}
                        <motion.div
                            className="absolute top-[200px] w-[240px] h-[150px] rounded-xl flex flex-col justify-between p-4 z-20 shadow-lg"
                            style={{
                                background: cardBackground,
                                border: `1px solid ${cardBorder}`,
                                boxShadow: '0 4px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.4)',
                            }}
                            initial={{ y: 200, opacity: 0 }}
                            animate={cardControls}
                        >
                            {/* Texture Overlay */}
                            <div className="absolute inset-0 bg-cover bg-center opacity-30 mix-blend-overlay rounded-xl"
                                style={{ backgroundImage: `url(${cardTexture})` }}
                            />

                            <div className="font-mono text-lg font-bold tracking-widest uppercase relative z-10" style={{ color: textColor }}>Paradise Club</div>
                            <div className="w-[105%] h-8 bg-[#2a2a2a] -ml-[2.5%] mt-2"></div>
                            <div className="self-end text-[8px] font-mono font-bold opacity-80 uppercase tracking-wider" style={{ color: textColor }}>
                                {labelText}
                            </div>
                        </motion.div>

                        {/* LAYER 4: The Top Mask (Upper Lip) */}
                        <div className="absolute top-[80px] w-full h-[38px] z-30 pointer-events-none overflow-hidden">
                            {/* Duplicate the Lip Style but cutoff the bottom */}
                            <div
                                className="w-[90%] mx-auto h-12 bg-[#333] rounded-t-md relative border-t border-gray-500"
                                style={{
                                    background: 'linear-gradient(to bottom, #444, #222)'
                                }}
                            >
                            </div>
                        </div>

                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default UnlockSequence;
